type Direction = 'prev' | 'next' | 'prevPage' | 'nextPage'
type FocusPosition = 'first' | 'last' | 'none'
// eslint-disable-next-line no-unused-vars,no-undef
type OnMonthChange = (direction: Direction) => void

export class RovingTabindexGrid {
  groupNode: Element
  gridCells: Element[]
  firstCell: Element | null
  lastCell: Element | null
  columnsPerRow: number
  onMonthChange?: OnMonthChange

  constructor(groupNode: Element, columnsPerRow: number = 7, onMonthChange?: OnMonthChange) {
    this.groupNode = groupNode
    this.columnsPerRow = columnsPerRow
    this.onMonthChange = onMonthChange
    this.gridCells = []
    this.firstCell = null
    this.lastCell = null

    this.initialize()
  }

  initialize(focusPosition: FocusPosition = 'none') {
    this.gridCells.forEach(cell => {
      cell.removeEventListener('keydown', this.handleKeydown)
      cell.removeEventListener('click', this.handleClick)
      cell.removeEventListener('focus', this.handleFocus)
      cell.removeEventListener('blur', this.handleBlur)
    })

    const cells = this.groupNode.querySelectorAll('[role=gridcell]:not([disabled])')

    this.gridCells = []
    this.firstCell = null
    this.lastCell = null

    for (let i = 0; i < cells.length; i++) {
      const cell = cells[i] as Element

      ;(cell as HTMLElement).tabIndex = -1

      cell.addEventListener('keydown', this.handleKeydown)
      cell.addEventListener('click', this.handleClick)
      cell.addEventListener('focus', this.handleFocus)
      cell.addEventListener('blur', this.handleBlur)

      this.gridCells.push(cell)

      if (!this.firstCell) {
        this.firstCell = cell
      }
      this.lastCell = cell
    }

    if (focusPosition === 'first' && this.firstCell) {
      ;(this.firstCell as HTMLElement).tabIndex = 0
      ;(this.firstCell as HTMLElement).focus()
    } else if (focusPosition === 'last' && this.lastCell) {
      ;(this.lastCell as HTMLElement).tabIndex = 0
      ;(this.lastCell as HTMLElement).focus()
    } else if (this.firstCell) {
      ;(this.firstCell as HTMLElement).tabIndex = 0
    }
  }

  destroy() {
    this.gridCells.forEach(cell => {
      cell.removeEventListener('keydown', this.handleKeydown)
      cell.removeEventListener('click', this.handleClick)
      cell.removeEventListener('focus', this.handleFocus)
      cell.removeEventListener('blur', this.handleBlur)
      ;(cell as HTMLElement).tabIndex = -1
    })

    this.gridCells = []
    this.firstCell = null
    this.lastCell = null
  }

  setFocused(currentItem: Element) {
    for (let i = 0; i < this.gridCells.length; i++) {
      ;(this.gridCells[i] as HTMLElement).tabIndex = -1
    }
    ;(currentItem as HTMLElement).tabIndex = 0
    ;(currentItem as HTMLElement).focus()
  }

  moveToPreviousCell(currentItem: Element) {
    const index = this.gridCells.indexOf(currentItem)
    if (index > 0) {
      this.setFocused(this.gridCells[index - 1])
    } else if (this.onMonthChange) {
      this.onMonthChange('prev')
    }
  }

  moveToNextCell(currentItem: Element) {
    const index = this.gridCells.indexOf(currentItem)
    if (index < this.gridCells.length - 1) {
      this.setFocused(this.gridCells[index + 1])
    } else if (this.onMonthChange) {
      this.onMonthChange('next')
    }
  }

  moveUp(currentItem: Element) {
    const index = this.gridCells.indexOf(currentItem)
    const targetIndex = index - this.columnsPerRow
    if (targetIndex >= 0) {
      this.setFocused(this.gridCells[targetIndex])
    } else if (this.onMonthChange) {
      this.onMonthChange('prev')
    }
  }

  moveDown(currentItem: Element) {
    const index = this.gridCells.indexOf(currentItem)
    const targetIndex = index + this.columnsPerRow
    if (targetIndex < this.gridCells.length) {
      this.setFocused(this.gridCells[targetIndex])
    } else if (this.onMonthChange) {
      this.onMonthChange('next')
    }
  }

  moveToStartOfRow(currentItem: Element) {
    const index = this.gridCells.indexOf(currentItem)
    const rowStart = Math.floor(index / this.columnsPerRow) * this.columnsPerRow
    this.setFocused(this.gridCells[rowStart])
  }

  moveToEndOfRow(currentItem: Element) {
    const index = this.gridCells.indexOf(currentItem)
    const rowEnd =
      Math.floor(index / this.columnsPerRow) * this.columnsPerRow + this.columnsPerRow - 1
    const targetIndex = Math.min(rowEnd, this.gridCells.length - 1)
    this.setFocused(this.gridCells[targetIndex])
  }

  private handleKeydown = (event: KeyboardEvent) => {
    const tgt = event.currentTarget as Element
    let flag = false

    switch (event.key) {
      case 'ArrowRight':
        this.moveToNextCell(tgt)
        flag = true
        break
      case 'ArrowLeft':
        this.moveToPreviousCell(tgt)
        flag = true
        break
      case 'ArrowDown':
        this.moveDown(tgt)
        flag = true
        break
      case 'ArrowUp':
        this.moveUp(tgt)
        flag = true
        break
      case 'Home':
        this.moveToStartOfRow(tgt)
        flag = true
        break
      case 'End':
        this.moveToEndOfRow(tgt)
        flag = true
        break
      case 'PageUp':
        if (this.onMonthChange) {
          this.onMonthChange('prevPage')
          flag = true
        }
        break
      case 'PageDown':
        if (this.onMonthChange) {
          this.onMonthChange('nextPage')
          flag = true
        }
        break
      case 'Enter':
      case ' ':
        tgt.dispatchEvent(new MouseEvent('click', { bubbles: true }))
        flag = true
        break
    }

    if (flag) {
      event.stopPropagation()
      event.preventDefault()
    }
  }

  private handleClick = (_event: MouseEvent) => {
    // Click handling is done by the component
  }

  private handleFocus = (event: FocusEvent) => {
    const tgt = event.currentTarget as Element
    tgt.classList.add('focus')
  }

  private handleBlur = (event: FocusEvent) => {
    const tgt = event.currentTarget as Element
    tgt.classList.remove('focus')
  }
}
